import { CURRENCY_CODES } from './Constants';
import type { applicationData, amount } from '../types/interfaces';

export const OPERACIONES_PUBLICAS_PRODUCT_CODE = '008';

export const SYNDICATED_CHECKBOX_HELPER_TEXT =
  'Al marcarla, la solicitud se limita al producto Operaciones Públicas y se deriva directamente a Carpetas Cerradas, sin pasar por Análisis de Riesgo, Comité ni Instrumentación.';

export const LATE_SYNDICATION_HELPER_TEXT =
  'No se puede marcar como sindicada porque la carpeta ya ingresó a Riesgo o Comité.';

const LATE_SYNDICATION_STATUS_CODES = new Set(['APP', 'RIS', 'COM']);

/** Extensible allowlist for checkbox visibility (role gates). */
export const SYNDICATED_CHECKBOX_ROLES = {
  COMERCIAL: true,
  COMITE: true,
  INSTRUMENTADOR: true,
} as const;

export type SyndicatedRoleGate = {
  isCommercial: boolean;
  isComite: boolean;
  isInstrumentator: boolean;
  isProducer: boolean;
};

export type CanMarkSyndicatedInput = {
  riskAnalysis?: unknown | null;
  products?: Array<{ source?: string | null } | null> | null;
  statusHistoryCodes?: Array<string | null | undefined> | null;
  currentStatusCode?: string | null;
  committeeApprovalHistory?: unknown[] | null;
};

export type CanShowSyndicatedCheckboxInput = {
  isSyndicatedPublicOperationsEnabled: boolean | undefined;
  roles: SyndicatedRoleGate;
  /** True once mid-alta / edit has a persisted application id. */
  hasApplicationId?: boolean;
  /** Create-time allowlist stamp from GET; null/false = Productor-born / historical. */
  syndicationEligibleAtCreate?: boolean | null;
  /** Already syndicated → chip+Cerrar only, no checkbox. */
  isSyndicated?: boolean;
  /** Late gate result from canMarkSyndicated (false = hide). */
  canMark?: boolean;
  /** CUIT gate for new create; default true (AC3 enforce elsewhere). */
  cuitOk?: boolean;
};

function isRoleInCheckboxAllowlist(roles: SyndicatedRoleGate): boolean {
  return (
    (SYNDICATED_CHECKBOX_ROLES.COMERCIAL && roles.isCommercial) ||
    (SYNDICATED_CHECKBOX_ROLES.COMITE && roles.isComite) ||
    (SYNDICATED_CHECKBOX_ROLES.INSTRUMENTADOR && roles.isInstrumentator)
  );
}

/**
 * Checkbox visibility for Operaciones Públicas sindicadas.
 * PRODUCTOR never sees it. Mid-alta keeps visibility only when eligible_at_create=true.
 * Already-syndicated and late folders hide the checkbox (chip/Cerrar path).
 */
export function canShowSyndicatedCheckbox(
  input: CanShowSyndicatedCheckboxInput
): boolean {
  const {
    isSyndicatedPublicOperationsEnabled,
    roles,
    hasApplicationId = false,
    syndicationEligibleAtCreate = null,
    isSyndicated = false,
    canMark = true,
    cuitOk = true,
  } = input;

  if (!isSyndicatedPublicOperationsEnabled) return false;
  if (!isRoleInCheckboxAllowlist(roles)) return false;
  if (isSyndicated) return false;
  if (!canMark) return false;
  if (!cuitOk) return false;
  if (hasApplicationId && syndicationEligibleAtCreate !== true) return false;
  return true;
}

/**
 * FE gate for remarcado: false if riskAnalysis OR RISK_ANALYSIS source OR
 * history/status APP|RIS|COM OR committee approval history present.
 * BE remains authoritative for disabled RISK_ANALYSIS rows.
 */
export function canMarkSyndicated(input: CanMarkSyndicatedInput): boolean {
  if (input.riskAnalysis) return false;
  if (input.products?.some((p) => p?.source === 'RISK_ANALYSIS')) return false;
  if (input.committeeApprovalHistory && input.committeeApprovalHistory.length > 0) {
    return false;
  }
  const codes = [
    ...(input.statusHistoryCodes ?? []),
    input.currentStatusCode,
  ].filter((code): code is string => !!code);
  if (codes.some((code) => LATE_SYNDICATION_STATUS_CODES.has(code))) return false;
  return true;
}

export function buildOperacionesPublicasLine(
  amountOverride?: amount
): applicationData {
  return {
    id: 0,
    product_line_code: OPERACIONES_PUBLICAS_PRODUCT_CODE,
    amount: amountOverride ?? {
      currency_code: CURRENCY_CODES.ars,
      value: 0,
    },
    has_assets: false,
  };
}

export function isSyndicatedClosePath(
  isSyndicatedPublicOperationsEnabled: boolean | undefined,
  isSyndicated: boolean | undefined
): boolean {
  return isSyndicatedPublicOperationsEnabled === true && isSyndicated === true;
}
